/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    makeStyles,
    useMediaQuery,
} from '@material-ui/core';
import { useUIDSeed } from 'react-uid';
import {
    arrayOf, func, shape, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from 'mbp-logger';

import { ArrowForwardIosRounded, ArrowBackIosRounded, Height } from '@material-ui/icons';
import { setCategoryTabs } from '../../../../../state/ducks/App/App-Actions';
import useSalesforcePersonalizedContentQuery from '../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import noop from '../../../../helpers/noop';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getSalesforceAttributes } from '../../../../../state/ducks/App/App-Selectors';

const useStyles = makeStyles((theme) => ({
    root: (styles) => ({
        maxWidth: styles.maxWidth || '',
        margin: styles?.margin || '5px auto 20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '0 10px',
        [theme.breakpoints.up(1024)]: {
            padding: '0 20px',
        },
        [theme.breakpoints.up(1400)]: {
            padding: '0',
        },
    }),
    wrapper: {
        width: '100%',
        cursor: 'pointer',
        display: 'flex',
    },
    tabsScroll: {
        display: 'flex',
        overflowX: 'auto',
        scrollBehavior: 'smooth',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    tab: (styles) => ({
        padding: '10px 20px',
        fontSize: styles?.font_size || '16px',
        fontFamily: styles?.font_family || 'inherit',
        cursor: 'pointer',
        borderBottom: `3px solid ${styles?.border_color?.color}`,
        whiteSpace: 'nowrap',
        fontWeight: '500',
        color: styles?.color?.color || '#767571',
        '&:hover': {
            borderBottom: `3px solid ${styles?.hover_color?.color}`,
            color: styles?.hover_color?.color || '#483f6b',
        },
    }),
    active: (styles) => ({
        borderBottom: `3px solid ${styles?.hover_color?.color}`,
        color: styles?.hover_color?.color || '#483f6b',
        fontWeight: '700',
    }),
    controls: (styles) => ({
        display: 'flex',
        gap: '10px',
        borderBottom: `3px solid ${styles?.border_color?.color}`,
        paddingLeft: '20px',
    }),
    button: (styles) => ({
        border: '0',
        cursor: 'pointer',
        background: 'none',
        '& svg': {
            fontSize: styles?.icon_font_size || '16px',
        },
    }),
    disabledControl: {
        opacity: '0.9',
        cursor: 'not-allowed',
        pointerEvents: 'none',
    },
    tabRoot: {
        margin: '32px auto',
        [theme.breakpoints.down(1400)]: {
            margin: '32px 20px',
        },
        [theme.breakpoints.down(1024)]: {
            margin: '32px 10px',
        },
    },
    tabContainer: {
        display: 'flex',
        width: '70%',
        height: '45px',
        margin: '10px 0 25px',
        borderBottom: '3px solid #E7EDF1',
        [theme.breakpoints.down(600)]: {
            width: '100%',
        },
    },
    tabBox: {
        display: 'flex',
        width: '15%',
        padding: '10px 20px',
        [theme.breakpoints.down(600)]: {
            minWidth: '30%',
            overflowX: 'scroll',
        },
    },
    tabText: {
        // margin: '0px 16px',
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        background: 'linear-gradient(270deg, #E7EDF1, #F2F8FC, #E7EDF1)',
        backgroundSize: '1000% 1000%',
    },
}));

const CategoryTabs = ({ maxWidth, categoryTabs, trackClickThrough }) => {
    const categoryStyles = categoryTabs?.styles || {};
    const classes = useStyles({
        maxWidth,
        ...categoryStyles,
    });
    const defaultSelectedIdx = (categoryTabs?.default_selected_index || 0) - 1;

    const dispatch = useDispatch();
    const seed = useUIDSeed();
    const scrollRef = useRef(null);
    const wrapperRef = useRef(null);
    const [categoryIndex, setCategoryIndex] = useState(defaultSelectedIdx);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 600px)');
    const salesforceAttributes = useSelector(getSalesforceAttributes);

    useEffect(() => {
        if (salesforceAttributes?.categoryTabs?.referenceProductCategory) {
            const idx = categoryTabs?.categories?.findIndex(
                (ele) => ele?.key === salesforceAttributes.categoryTabs.referenceProductCategory
            );
            setCategoryIndex(idx);
        }
    }, [salesforceAttributes?.categoryTabs]);

    useEffect(() => {
        const updateScrollButtons = () => {
            const el = scrollRef.current;
            if (!el) return;
            setCanScrollPrev(el.scrollLeft > 0);
            setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth);
        };

        updateScrollButtons();
        scrollRef.current?.addEventListener('scroll', updateScrollButtons);
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateScrollButtons);
        }

        return () => {
            scrollRef.current?.removeEventListener('scroll', updateScrollButtons);
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateScrollButtons);
            }
        };
    }, [categoryTabs]);

    const handleClick = (index) => {
        const category = categoryTabs?.categories[index];

        if (categoryIndex === index) {
            setCategoryIndex(defaultSelectedIdx);
            dispatch(
                setCategoryTabs({
                    referenceProductID: '',
                    referenceProductCategory: '',
                }),
            );
            return;
        }

        trackClickThrough(category?.key);

        dispatch(
            setCategoryTabs({
                referenceProductID: category?.product_id,
                referenceProductCategory: category?.key,
            }),
        );
        setCategoryIndex(index);
    };

    const handlePrev = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const handleNext = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    if (categoryTabs?.categories?.length > 0) {
        const showControls = isDesktop && (canScrollNext || canScrollPrev);

        return (
            <div className={classes.root}>
                <div className={classes.wrapper} ref={wrapperRef}>
                    <div className={classes.tabsScroll} ref={scrollRef}>
                        {categoryTabs.categories.map((item, index) => (
                            <div
                                key={seed(item)}
                                className={`${classes.tab} ${categoryIndex === index ? classes.active : ''}`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={() => handleClick(index)}
                                onClick={() => handleClick(index)}
                            >
                                {item?.title}
                            </div>
                        ))}
                    </div>

                    {
                        showControls && (
                            <div className={classes.controls}>
                                <button
                                    className={`${classes.button} ${!canScrollPrev ? classes.disabledControl : ''}`}
                                    type="button"
                                    onClick={handlePrev}
                                    disabled={!canScrollPrev}
                                >
                                    <ArrowBackIosRounded />
                                </button>
                                <button
                                    className={`${classes.button} ${!canScrollNext ? classes.disabledControl : ''}`}
                                    type="button"
                                    onClick={handleNext}
                                    disabled={!canScrollNext}
                                >
                                    <ArrowForwardIosRounded />
                                </button>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    }

    return null;
};

CategoryTabs.propTypes = {
    maxWidth: string,
    categoryTabs: shape({
        categories: arrayOf(
            shape({
                label: string,
            })),
    }),
    trackClickThrough: func,
};

CategoryTabs.defaultProps = {
    maxWidth: '',
    categoryTabs: { categories: [] },
    trackClickThrough: noop,
};

const NavigationPillsContainer = ({
    campaignId, maxWidth,
}) => {
    if (!campaignId) return null;
    const dispatch = useDispatch();
    const classes = useStyles();

    const {
        loading, error, data, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'NavigationPills',
        interactionName: campaignId,
        targeting: {
            pageType: 'home',
        },
        optInHoldoutParticipation: true,
        resolveIdentity: true,
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: data?.contentSF,
        isFireImpression: true,
        page: { type: 'home' },
    });

    if (loading || variables?.skip) {
        return (
            <div className={classes.tabRoot}>
                <div className={classes.tabContainer}>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        mbpLogger.logError({
            function: `NavigationPillsContainer - campaignId = ${campaignId}`,
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'NavigationPillsContainer',
            jsError: error,
        });
        return null;
    }

    const handleClick = (category) => {
        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            category,
            salesforceResponse: data?.contentSF,
            page: { type: 'home' },
            eventSubType: 'campaign-tracking',
        }));
    };
    if (data?.contentSF?.content) {
        const sfCMSData = data.contentSF.content;

        return (
            <CategoryTabs maxWidth={maxWidth} categoryTabs={sfCMSData} trackClickThrough={handleClick} />
        );
    }
    return null;
};

NavigationPillsContainer.propTypes = {
    maxWidth: string,
    campaignId: string,
};

NavigationPillsContainer.defaultProps = {
    maxWidth: '',
    campaignId: '',
};

export default NavigationPillsContainer;
